import { useState, useEffect, useMemo, useRef } from "react"
import {
  Users as UsersIcon,
  UserCheck,
  Smartphone,
  Crown,
  Clock,
  ArrowUpDown,
  AlertTriangle,
  Upload,
  ShieldCheck,
  ShieldX,
  Search,
  Download,
  HelpCircle,
  Plus,
  X,
  SlidersHorizontal,
  Check,
  Trash2,
  StickyNote,
} from "lucide-react"
import {
  getUnifiedUsers,
  setAccess,
  setFounding,
  addUser,
  addUsersBulk,
  deleteUnifiedUser,
  getAdminNotes,
  setAdminNote,
  type UnifiedUser,
  type NewUserInput,
  type AdminNote,
} from "@/lib/api"
import { toast } from "sonner"
import { cn, phoneMatches } from "@/lib/utils"
import { downloadCsv, parseCsv } from "@/lib/csv"
import { useReadOnly } from "@/lib/read-only"
import { useAuth } from "@/lib/auth"
import { SafetyDialog } from "@/components/ConfirmDialog"

/** E.164 digits (no '+'); 10-digit assumed US/CA; matches backend is_valid_phone. */
function normalizePhone(raw?: string | null): string {
  const digits = (raw ?? "").replace(/\D/g, "")
  if (digits.length === 10) return "1" + digits
  if (digits.length >= 8 && digits.length <= 15) return digits
  return ""
}

function formatPhone(p?: string | null): string {
  const n = normalizePhone(p)
  if (!n) return p || "—"
  if (n.length === 11 && n.startsWith("1")) {
    return `+1 (${n.slice(1, 4)}) ${n.slice(4, 7)}-${n.slice(7)}`
  }
  return "+" + n // international -- formats vary by country, show plain E.164
}

function toE164(p?: string | null): string {
  const n = normalizePhone(p)
  return n ? "+" + n : ""
}

function formatTimestamp(iso?: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (isNaN(d.getTime())) return "—"
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
}

function tsSafe(iso?: string | null): number {
  const t = Date.parse(iso ?? "")
  return isNaN(t) ? 0 : t
}

function fullName(first?: string | null, last?: string | null): string {
  return [first, last].filter(Boolean).join(" ").trim() || "—"
}

function pick(rec: Record<string, string>, ...names: string[]): string {
  for (const n of names) { const v = rec[n.toLowerCase()]; if (v) return v }
  return ""
}

// phone-only CSV batch-approves existing waitlisters; firstName only needed for new rows.
const CSV_REQUIRED = ["phoneNumber"]
const CSV_OPTIONAL = ["firstName", "lastName", "email", "city", "referral", "tags"]
const CSV_TEMPLATE_HEADERS = [...CSV_REQUIRED, ...CSV_OPTIONAL]

// Backend column is 'annual', not store 'yearly' — keys must match revenuecat_service.py.
const PLAN_LABELS: Record<string, string> = {
  founding_member: "Founding",
  weekly: "Weekly",
  monthly: "Monthly",
  annual: "Annual",
}

function planLabel(plan?: string | null, status?: string | null): string {
  if (plan && PLAN_LABELS[plan]) return PLAN_LABELS[plan]
  if (status && status !== "free") return status
  return "Free"
}

// Shared via admin_notes API; localStorage fallback when notes endpoints aren't deployed.
const NOTES_STORAGE_KEY = "offseason-admin-user-notes"

function loadLocalNotes(): Record<string, string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(NOTES_STORAGE_KEY) ?? "{}")
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {}
  } catch { return {} }
}

function persistLocalNotes(notes: Record<string, string>) {
  try { localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes)) } catch { /* storage full/blocked */ }
}

function noteTooltip(note: string, meta?: AdminNote): string {
  const author = meta?.updated_by ? `\n\n— ${meta.updated_by}${meta.updated_at ? `, ${formatTimestamp(meta.updated_at)}` : ""}` : ""
  return `${note}${author}\n\nClick to edit`
}

function NotesCell({ note, meta, disabled, onSave }: { note: string; meta?: AdminNote; disabled?: boolean; onSave: (text: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note)
  const start = () => { setDraft(note); setEditing(true) }
  const save = () => { onSave(draft.trim()); setEditing(false) }

  if (editing) {
    return (
      <div className="min-w-[220px] space-y-1.5">
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save()
            if (e.key === "Escape") setEditing(false)
          }}
          placeholder="Add context about this person…"
          className="w-full px-2 py-1.5 text-xs border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-primary resize-y"
        />
        <div className="flex items-center gap-1.5">
          <button onClick={save}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded bg-primary text-primary-foreground hover:bg-primary/90">
            <Check className="size-3" /> Save
          </button>
          <button onClick={() => setEditing(false)} className="px-2 py-0.5 text-[11px] rounded border hover:bg-accent">Cancel</button>
        </div>
      </div>
    )
  }
  if (!note) {
    return (
      <button onClick={start} disabled={disabled} title={disabled ? "Read-only mode" : "Add a note (shared with all admins)"}
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-dashed text-muted-foreground/60 hover:text-foreground hover:bg-accent disabled:opacity-40">
        <StickyNote className="size-3" /> Note
      </button>
    )
  }
  return (
    <button onClick={start} disabled={disabled} title={disabled ? note : noteTooltip(note, meta)}
      className="flex items-start gap-1.5 text-left max-w-[240px] rounded px-1.5 py-1 -mx-1.5 hover:bg-accent/60 disabled:opacity-60">
      <StickyNote className="size-3 mt-0.5 shrink-0 text-amber-500" />
      <span className="text-xs text-foreground/90 line-clamp-2 whitespace-pre-wrap wrap-break-word">{note}</span>
    </button>
  )
}

function HelpHint({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative inline-flex">
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
        className="text-muted-foreground/60 hover:text-foreground" title="What is this?" aria-label="Help">
        <HelpCircle className="size-4" />
      </button>
      {open && (
        <span className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-80 rounded-md border bg-popover px-3 py-2 text-xs font-normal text-popover-foreground shadow-md">
          {text}
        </span>
      )}
    </span>
  )
}

function StatCard({ icon: Icon, label, value, accent }: { icon: typeof UsersIcon; label: string; value: number; accent: string }) {
  return (
    <div className="rounded-lg border p-4 flex items-center gap-3">
      <div className={cn("rounded-md p-2", accent)}><Icon className="size-5" /></div>
      <div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  )
}

// One status per person: the furthest stage they've reached along
// Waitlisted -> Allowlisted -> Onboarded. Not a set of memberships.
type UserStatus = "onboarded" | "allowlisted" | "waitlist"

function statusLabel(u: UnifiedUser): string {
  const s = statusOf(u)
  return s ? STATUS_BADGE[s].label : ""
}

function statusOf(u: UnifiedUser): UserStatus | null {
  if (u.is_member) return "onboarded"
  if (u.in_allowlist) return "allowlisted"
  if (u.in_waitlist) return "waitlist"
  return null
}

const STATUS_BADGE: Record<UserStatus, { label: string; cls: string }> = {
  onboarded: { label: "Onboarded", cls: "bg-purple-100 text-purple-700 border-purple-200" },
  allowlisted: { label: "Allowlisted", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  waitlist: { label: "Waitlisted", cls: "bg-amber-100 text-amber-700 border-amber-200" },
}

function TypeBadges({ u }: { u: UnifiedUser }) {
  const status = statusOf(u)
  if (!status) return <span className="text-muted-foreground">—</span>
  const b = STATUS_BADGE[status]
  return (
    <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border", b.cls)}>{b.label}</span>
  )
}

function AddUserModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [form, setForm] = useState<NewUserInput>({ first_name: "", phone_number: "" })
  const [saving, setSaving] = useState(false)
  const set = (k: keyof NewUserInput) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const submit = async () => {
    if (!form.first_name.trim()) return toast.error("First name is required")
    if (!normalizePhone(form.phone_number)) return toast.error("A valid phone number is required")
    setSaving(true)
    try {
      await addUser(form)
      toast.success(`Added ${form.first_name}`)
      onAdded(); onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add user")
    } finally { setSaving(false) }
  }

  const field = (k: keyof NewUserInput, label: string, required = false) => (
    <label className="block text-sm">
      <span className="text-muted-foreground">{label}{required && " *"}</span>
      <input value={(form[k] as string) ?? ""} onChange={set(k)}
        className="mt-1 w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-primary" />
    </label>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Add user</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="size-4" /></button>
        </div>
        <div className="space-y-3">
          {field("first_name", "First name", true)}
          {field("last_name", "Last name")}
          {field("phone_number", "Phone", true)}
          {field("email", "Email")}
          {field("city", "City")}
          {field("referral", "Referral")}
        </div>
        <p className="text-xs text-muted-foreground mt-3">Added to the waitlist (access stays off until you approve it).</p>
        <div className="flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-md text-sm hover:bg-accent">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="flex-1 px-4 py-2 rounded-md text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50">
            {saving ? "Adding…" : "Add user"}
          </button>
        </div>
      </div>
    </div>
  )
}

type SortKey = "name" | "signup"

export function WaitlistPage() {
  const { readOnly } = useReadOnly()
  const [users, setUsers] = useState<UnifiedUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [sortKey, setSortKey] = useState<SortKey>("signup")
  const [sortAsc, setSortAsc] = useState(false)
  const [busy, setBusy] = useState<Set<string>>(new Set())
  const [showAdd, setShowAdd] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<UnifiedUser | null>(null)
  const [importApproveAccess, setImportApproveAccess] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const { user: authUser } = useAuth()
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [notesMeta, setNotesMeta] = useState<Record<string, AdminNote>>({})
  const [notesLocalOnly, setNotesLocalOnly] = useState(false)

  const fetchNotes = async () => {
    try {
      const server = await getAdminNotes()
      setNotes(Object.fromEntries(Object.entries(server).map(([p, n]) => [p, n.note])))
      setNotesMeta(server)
      setNotesLocalOnly(false)
    } catch {
      // Backend without the notes endpoints (or unreachable) — browser-local mode.
      setNotes(loadLocalNotes())
      setNotesMeta({})
      setNotesLocalOnly(true)
    }
  }

  const saveNote = async (phone: string, text: string) => {
    const apply = (prev: Record<string, string>) => {
      const next = { ...prev }
      if (text) next[phone] = text
      else delete next[phone]
      return next
    }
    if (notesLocalOnly) {
      setNotes((prev) => { const next = apply(prev); persistLocalNotes(next); return next })
      toast.warning("Notes backend not available — saved to this browser only")
      return
    }
    const previous = notes
    setNotes(apply)
    try {
      await setAdminNote(phone, text, authUser?.email ?? undefined)
      setNotesMeta((prev) => ({
        ...prev,
        [phone]: { note: text, updated_by: authUser?.email ?? null, updated_at: new Date().toISOString() },
      }))
    } catch (err) {
      setNotes(previous)
      toast.error(err instanceof Error ? err.message : "Failed to save note")
    }
  }

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [anchorPhone, setAnchorPhone] = useState<string | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [confirmBulk, setConfirmBulk] = useState<"revoke" | "delete" | null>(null)

  const [filterOpen, setFilterOpen] = useState(false)
  const [fType, setFType] = useState<Set<string>>(new Set())
  const [fAccess, setFAccess] = useState<"all" | "approved" | "off">("all")
  const [fFounding, setFFounding] = useState<"all" | "yes" | "no">("all")
  const [fPlan, setFPlan] = useState<Set<string>>(new Set())
  const activeFilters =
    (fType.size ? 1 : 0) + (fAccess !== "all" ? 1 : 0) + (fFounding !== "all" ? 1 : 0) + (fPlan.size ? 1 : 0)
  const clearFilters = () => { setFType(new Set()); setFAccess("all"); setFFounding("all"); setFPlan(new Set()) }
  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, v: string) =>
    setter((prev) => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n })
  // Any plan the backend recognises buckets to itself; everything else (NULL,
  // or a product id we haven't mapped yet) buckets to "free" for filtering.
  const planKey = (u: UnifiedUser) =>
    u.subscription_plan && PLAN_LABELS[u.subscription_plan] ? u.subscription_plan : "free"
  const matchesFilters = (u: UnifiedUser) => {
    // Filter on the single displayed status, so "Waitlist" matches the pill.
    if (fType.size) {
      const s = statusOf(u)
      if (!s || !fType.has(s)) return false
    }
    if (fAccess === "approved" && !u.access) return false
    if (fAccess === "off" && u.access) return false
    if (fFounding === "yes" && !u.founding) return false
    if (fFounding === "no" && u.founding) return false
    if (fPlan.size && !fPlan.has(planKey(u))) return false
    return true
  }

  const fetchAll = async () => {
    setIsLoading(true); setError(null)
    try {
      const [loadedUsers] = await Promise.all([getUnifiedUsers(), fetchNotes()])
      setUsers(loadedUsers)
    }
    catch (err) { setError(err instanceof Error ? err.message : "Failed to load users") }
    finally { setIsLoading(false) }
  }
  useEffect(() => { fetchAll() }, [])

  const withBusy = async (phone: string, fn: () => Promise<void>) => {
    setBusy((p) => new Set(p).add(phone))
    try { await fn() }
    finally { setBusy((p) => { const n = new Set(p); n.delete(phone); return n }) }
  }

  // Same five words the dashboard uses, in funnel order: Waitlist → Waiting →
  // Approved → Accounts → Users. "Accounts" is everyone who made an in-app
  // account and is the ONLY thing that word means anywhere in the admin UI;
  // "Users" is the subset who finished onboarding. This card used to label the
  // account count "Onboarded" — 91 against the ~47 who actually finished, so
  // the two pages disagreed by 44.
  const stats = useMemo(() => {
    // Our own QA bots stay visible in the table but out of the headline counts,
    // so this page and the dashboard are counting the same people.
    const real = users.filter((u) => !u.internal)
    return {
      total: real.length,
      approved: real.filter((u) => u.access).length,
      // Approved accounts only, matching the dashboard's Accounts rung: a
      // handful of rows reached signup without ever being approved, and the
      // dashboard reports those separately as never_approved rather than here.
      members: real.filter((u) => u.access && u.is_member).length,
      users: real.filter((u) => u.onboarded_at).length,
      founding: real.filter((u) => u.founding).length,
      // Waiting = on the list, not approved yet. The in_waitlist check is what
      // keeps this off-by-one-free against the dashboard: a row can exist
      // without access AND without ever having been on the waitlist.
      waitlist: real.filter((u) => u.in_waitlist && !u.access).length,
    }
  }, [users])

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = users.filter((u) => {
      if (q && ![u.first_name, u.last_name, u.phone_number, u.email, u.city, u.referral, u.member_reason, notes[u.phone_number]]
        .filter(Boolean).join(" ").toLowerCase().includes(q) && !phoneMatches(u.phone_number, q)) return false
      return matchesFilters(u)
    })
    return [...filtered].sort((a, b) => {
      const dir = sortAsc ? 1 : -1
      if (sortKey === "name") return fullName(a.first_name, a.last_name).localeCompare(fullName(b.first_name, b.last_name)) * dir
      return (tsSafe(a.created_at) - tsSafe(b.created_at)) * dir
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, search, sortKey, sortAsc, fType, fAccess, fFounding, fPlan, notes])

  const toggleSort = (k: SortKey) => { if (sortKey === k) setSortAsc((v) => !v); else { setSortKey(k); setSortAsc(k === "name") } }

  // Prune selection to visible rows when filter/search/sort changes.
  useEffect(() => {
    const vis = new Set(rows.map((r) => r.phone_number))
    setSelected((prev) => {
      const next = new Set([...prev].filter((p) => vis.has(p)))
      return next.size === prev.size ? prev : next
    })
  }, [rows])

  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.has(r.phone_number))
  const someVisibleSelected = rows.some((r) => selected.has(r.phone_number))
  const selectedUsers = useMemo(() => users.filter((u) => selected.has(u.phone_number)), [users, selected])

  const onRowSelect = (e: React.MouseEvent, u: UnifiedUser, idx: number) => {
    const phone = u.phone_number
    const anchorIdx = anchorPhone != null ? rows.findIndex((r) => r.phone_number === anchorPhone) : -1
    if (e.shiftKey && anchorIdx >= 0) {
      const [a, b] = anchorIdx <= idx ? [anchorIdx, idx] : [idx, anchorIdx]
      const range = rows.slice(a, b + 1).map((r) => r.phone_number)
      setSelected((prev) => new Set([...prev, ...range]))
    } else {
      setSelected((prev) => { const n = new Set(prev); n.has(phone) ? n.delete(phone) : n.add(phone); return n })
      setAnchorPhone(phone)
    }
  }
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (allVisibleSelected) {
        const n = new Set(prev); rows.forEach((r) => n.delete(r.phone_number)); return n
      }
      return new Set([...prev, ...rows.map((r) => r.phone_number)])
    })
  }
  const clearSelection = () => { setSelected(new Set()); setAnchorPhone(null) }

  const runBulk = async (label: string, fn: (u: UnifiedUser) => Promise<unknown>, onEachOk?: (u: UnifiedUser) => void) => {
    if (readOnly || bulkBusy || selectedUsers.length === 0) return
    setBulkBusy(true)
    let ok = 0, fail = 0
    try {
      for (const u of selectedUsers) {
        try { await fn(u); onEachOk?.(u); ok++ } catch { fail++ }
      }
    } finally { setBulkBusy(false) }
    toast[fail ? "warning" : "success"](`${label}: ${ok} done${fail ? `, ${fail} failed` : ""}`)
    await fetchAll()
  }
  const bulkAccess = (approve: boolean) =>
    runBulk(approve ? "Approved access" : "Revoked access",
      (u) => setAccess(u.phone_number, approve, u.first_name ?? undefined, u.last_name ?? undefined))
  const bulkFounding = (on: boolean) =>
    runBulk(on ? "Marked founding" : "Unmarked founding",
      (u) => setFounding(u.phone_number, on, u.user_id))
  const bulkDelete = async () => {
    const removed: string[] = []
    await runBulk("Deleted", (u) => deleteUnifiedUser(u.phone_number, u.user_id), (u) => removed.push(u.phone_number))
    setSelected(new Set())
  }
  const bulkExport = () => {
    if (selectedUsers.length === 0) return
    downloadCsv(
      "users-selected.csv",
      ["firstName", "lastName", "phone", "email", "city", "reason", "referral", "status", "access", "founding", "plan", "signedUp", "notes"],
      selectedUsers.map((u) => [
        u.first_name ?? "", u.last_name ?? "", toE164(u.phone_number), u.email ?? "", u.city ?? "", u.member_reason ?? "", u.referral ?? "",
        statusLabel(u),
        u.access ? "approved" : "no", u.founding ? "yes" : "no", u.subscription_plan ?? "none", u.created_at ?? "",
        notes[u.phone_number] ?? "",
      ]),
    )
    toast.success(`Exported ${selectedUsers.length} selected`)
  }
  const selectionHasMember = selectedUsers.some((u) => u.is_member)

  const doDelete = (u: UnifiedUser) => withBusy(u.phone_number, async () => {
    if (readOnly) return
    try {
      const res = await deleteUnifiedUser(u.phone_number, u.user_id)
      setUsers((prev) => prev.filter((x) => x.phone_number !== u.phone_number))
      toast.success(`Deleted ${fullName(u.first_name, u.last_name)}${res.account_deleted ? " · account + all data" : ""}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete user")
    }
  })

  const onToggleAccess = (u: UnifiedUser) => withBusy(u.phone_number, async () => {
    const next = !u.access
    try {
      await setAccess(u.phone_number, next, u.first_name ?? undefined, u.last_name ?? undefined)
      setUsers((prev) => {
        if (next) {
          return prev.map((x) => x.phone_number === u.phone_number ? { ...x, access: true, in_allowlist: true } : x)
        }
        // Revoke drops allowlist; row vanishes if not a member and not on waitlist.
        return prev.flatMap((x) => {
          if (x.phone_number !== u.phone_number) return [x]
          if (!x.is_member && !x.in_waitlist) return []
          return [{ ...x, access: false, in_allowlist: false }]
        })
      })
      toast.success(`${next ? "Approved" : "Revoked"} access for ${formatPhone(u.phone_number)}`)
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to set access") }
  })

  const onToggleFounding = (u: UnifiedUser) => withBusy(u.phone_number, async () => {
    const next = !u.founding
    try {
      await setFounding(u.phone_number, next, u.user_id)
      setUsers((prev) => prev.map((x) => {
        if (x.phone_number !== u.phone_number) return x
        const plan = x.is_member ? (next ? "founding_member" : null) : x.subscription_plan
        const status = x.is_member ? (next ? "active" : "free") : x.subscription_status
        return { ...x, founding: next, access: next ? true : x.access, in_allowlist: true, subscription_plan: plan, subscription_status: status }
      }))
      toast.success(`${next ? "Marked" : "Unmarked"} founding for ${fullName(u.first_name, u.last_name)}`)
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to set founding") }
  })

  const onImportFile = async (file: File) => {
    try {
      const { headers, rows: recs } = parseCsv(await file.text())
      const missing = CSV_REQUIRED.filter((c) => !headers.includes(c.toLowerCase()))
      if (missing.length) {
        toast.error(`CSV is missing required column(s): ${missing.join(", ")}. Download the template.`)
        return
      }
      // Already-listed phones go straight to access approval — never a second
      // waitlist row (the backend refuses one anyway, to protect the signup date).
      const known = new Map(users.map((u) => [normalizePhone(u.phone_number), u]))
      const seen = new Set<string>()
      const toAdd: NewUserInput[] = []
      const toApprove: { phone: string; first?: string; last?: string }[] = []
      let skipped = 0
      for (const rec of recs) {
        const phone = normalizePhone(pick(rec, "phoneNumber", "phone", "phone_number"))
        const first = pick(rec, "firstName", "first_name")
        const last = pick(rec, "lastName", "last_name")
        const existing = known.get(phone)
        if (!phone || seen.has(phone) || (!existing && !first)) { skipped++; continue }
        seen.add(phone)
        if (existing) {
          toApprove.push({ phone, first: first || existing.first_name || undefined, last: last || existing.last_name || undefined })
          continue
        }
        const tags = pick(rec, "tags").split(",").map((t) => t.trim()).filter(Boolean)
        toAdd.push({
          first_name: first,
          last_name: last || undefined,
          phone_number: phone,
          email: pick(rec, "email") || undefined,
          city: pick(rec, "city") || undefined,
          referral: pick(rec, "referral") || undefined,
          tags: tags.length ? tags : undefined,
        })
        toApprove.push({ phone, first, last: last || undefined })
      }
      if (!toAdd.length && !toApprove.length) {
        toast.error("No valid rows (need phoneNumber, plus firstName for anyone not already listed)")
        return
      }
      const res = toAdd.length ? await addUsersBulk(toAdd) : { added: 0, skipped: 0, already_listed: 0, merged: 0 }
      let accessFailed = 0
      if (importApproveAccess) {
        const t = toast.loading(`Approving access 0/${toApprove.length}…`)
        for (const [i, u] of toApprove.entries()) {
          try { await setAccess(u.phone, true, u.first, u.last) } catch { accessFailed++ }
          toast.loading(`Approving access ${i + 1}/${toApprove.length}…`, { id: t })
        }
        toast.dismiss(t)
      }
      const accessNote = importApproveAccess
        ? ` · access approved for ${toApprove.length - accessFailed}${accessFailed ? ` (${accessFailed} failed)` : ""}`
        : ""
      const totalSkipped = skipped + res.skipped
      // already_listed = already on the waitlist; the import merges into their
      // original row (filling blanks only) instead of writing a second one, so
      // the date they actually joined survives.
      const mergedNote = res.merged ? `, ${res.merged} updated with new details` : ""
      const alreadyNote = res.already_listed
        ? ` · ${res.already_listed} already on the waitlist (kept original signup date${mergedNote})`
        : ""
      const msg = `Added ${res.added} new${totalSkipped ? `, skipped ${totalSkipped}` : ""}${alreadyNote}${accessNote}`
      if (accessFailed) toast.warning(msg); else toast.success(msg)
      await fetchAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to import CSV")
    }
  }

  const downloadTemplate = () => downloadCsv("users-template.csv", CSV_TEMPLATE_HEADERS, [
    ["Jane", "16505550123", "Doe", "jane@example.com", "San Francisco", "Instagram", "Beta"],
  ])

  const exportUsers = () => downloadCsv(
    "users.csv",
    ["firstName", "lastName", "phone", "email", "city", "reason", "referral", "status", "access", "founding", "plan", "signedUp", "notes"],
    rows.map((u) => [
      u.first_name ?? "", u.last_name ?? "", toE164(u.phone_number), u.email ?? "", u.city ?? "", u.member_reason ?? "", u.referral ?? "",
      statusLabel(u),
      u.access ? "approved" : "no",
      u.founding ? "yes" : "no",
      u.subscription_plan ?? "none",
      u.created_at ?? "",
      notes[u.phone_number] ?? "",
    ]),
  )

  if (isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Users &amp; Access</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">{[1, 2, 3, 4].map((i) => <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />)}</div>
        <div className="space-y-2">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-12 rounded-md bg-muted animate-pulse" />)}</div>
      </div>
    )
  }
  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Users &amp; Access</h1>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
          <AlertTriangle className="size-8 mx-auto mb-2 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <UsersIcon className="size-6 text-muted-foreground" />
        <h1 className="text-2xl font-bold">Users &amp; Access</h1>
        <HelpHint text="One table for everyone, in funnel order. Waitlist = every row we have. Waiting = no access yet. Approved = allowed in. Accounts = made an in-app account. Users = finished onboarding. Access (the toggle) is the single source of truth for whether someone can use the app; subscription is separate from access." />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={UsersIcon} label="Waitlist" value={stats.total} accent="bg-slate-100 text-slate-700" />
        <StatCard icon={Clock} label="Waiting" value={stats.waitlist} accent="bg-amber-100 text-amber-700" />
        <StatCard icon={ShieldCheck} label="Approved" value={stats.approved} accent="bg-green-100 text-green-700" />
        <StatCard icon={Smartphone} label="Accounts" value={stats.members} accent="bg-cyan-100 text-cyan-700" />
        <StatCard icon={UserCheck} label="Users" value={stats.users} accent="bg-blue-100 text-blue-700" />
        <StatCard icon={Crown} label="Founding" value={stats.founding} accent="bg-purple-100 text-purple-700" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <input type="text" placeholder="Search name, phone, email, referral…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-primary" />
        </div>

        <div className="relative">
          <button onClick={() => setFilterOpen((v) => !v)}
            className={cn("inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md hover:bg-accent",
              activeFilters > 0 && "border-primary text-primary bg-primary/5")}>
            <SlidersHorizontal className="size-4" /> Filter
            {activeFilters > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">{activeFilters}</span>
            )}
          </button>
          {filterOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)} />
              <div className="absolute left-0 mt-2 z-20 w-72 rounded-lg border bg-popover text-popover-foreground shadow-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">Filters</span>
                  <button onClick={() => setFilterOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="size-4" /></button>
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1.5">Type</div>
                  <div className="space-y-1">
                    {[["onboarded", "Onboarded"], ["allowlisted", "Allowlisted"], ["waitlist", "Waitlisted"]].map(([v, label]) => (
                      <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={fType.has(v)} onChange={() => toggleSet(setFType, v)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1.5">Access</div>
                  <div className="inline-flex rounded-md border overflow-hidden text-xs">
                    {(["all", "approved", "off"] as const).map((v) => (
                      <button key={v} onClick={() => setFAccess(v)}
                        className={cn("px-2.5 py-1 capitalize", fAccess === v ? "bg-primary text-primary-foreground" : "hover:bg-accent")}>{v}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1.5">Founding</div>
                  <div className="inline-flex rounded-md border overflow-hidden text-xs">
                    {(["all", "yes", "no"] as const).map((v) => (
                      <button key={v} onClick={() => setFFounding(v)}
                        className={cn("px-2.5 py-1 capitalize", fFounding === v ? "bg-primary text-primary-foreground" : "hover:bg-accent")}>{v}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1.5">Plan</div>
                  <div className="space-y-1">
                    {[...Object.entries(PLAN_LABELS), ["free", "Free / none"]].map(([v, label]) => (
                      <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={fPlan.has(v)} onChange={() => toggleSet(setFPlan, v)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1 border-t">
                  <button onClick={clearFilters} disabled={activeFilters === 0}
                    className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40">Clear all</button>
                  <button onClick={() => setFilterOpen(false)}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90">
                    <Check className="size-3" /> Done
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <button onClick={() => setShowAdd(true)} disabled={readOnly}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50">
          <Plus className="size-4" /> Add user
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onImportFile(f); e.target.value = "" }} />
        <button onClick={() => fileRef.current?.click()} disabled={readOnly}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md hover:bg-accent disabled:opacity-50">
          <Upload className="size-4" /> Import CSV
        </button>
        <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md hover:bg-accent" title="Download a blank CSV template with the required columns">
          Template
        </button>
        <button onClick={exportUsers} disabled={rows.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md hover:bg-accent disabled:opacity-50">
          <Download className="size-4" /> Export CSV
        </button>
      </div>

      {/* Its own line so it never gets clipped off the end of the toolbar. */}
      <label className="-mt-2 flex items-center gap-2 text-sm cursor-pointer w-fit">
        <input type="checkbox" aria-label="Approve access for everyone in the imported file"
          checked={importApproveAccess} onChange={(e) => setImportApproveAccess(e.target.checked)} />
        Approve access for everyone in the imported file
        <span className="text-xs text-muted-foreground">
          — a CSV of just phone numbers approves existing people in bulk and adds no new rows
        </span>
      </label>

      <div className="rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  aria-label="Select all visible"
                  className="cursor-pointer align-middle"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected }}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <button onClick={() => toggleSort("name")} className="flex items-center gap-1 text-muted-foreground hover:text-foreground">Name <ArrowUpDown className="size-3" /></button>
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Phone</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">City</th>
              <th className="text-left px-4 py-3 font-medium">
                <span className="inline-flex items-center gap-1 text-muted-foreground">Reason <HelpHint text="What the member told the coach about why they want in. This is not an admin note." /></span>
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Referral</th>
              <th className="text-left px-4 py-3 font-medium">
                <button onClick={() => toggleSort("signup")} className="flex items-center gap-1 text-muted-foreground hover:text-foreground" title="Sort by signup time">Signed up <ArrowUpDown className="size-3" /></button>
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <span className="inline-flex items-center gap-1 text-muted-foreground">Access <HelpHint text="The single source of truth for app access. Approved = this phone can use the app (when invite-only is on). Toggling this never changes the subscription." /></span>
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <span className="inline-flex items-center gap-1 text-muted-foreground">Founding <HelpHint text="Founding is a label for early members; every member has full access. This is an explicit flag (not everyone with access is founding). Works even before the user has an account — they're recognized as founding at signup. Never changes access." /></span>
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <span className="inline-flex items-center gap-1 text-muted-foreground">Plan <HelpHint text="The actual subscription, read-only. Monthly/Yearly come from RevenueCat when the user subscribes; Founding = free. Set founding with the toggle on the left." /></span>
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <span className="inline-flex items-center gap-1 text-muted-foreground">Notes <HelpHint text="Admin notes for context (e.g. who a waitlister is, how you know them). Shared with all admins — saved to the backend (admin_notes table) with who wrote them and when. Included in Export CSV." /></span>
              </th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u, idx) => {
              const isBusy = busy.has(u.phone_number)
              const isSel = selected.has(u.phone_number)
              return (
                <tr key={u.phone_number} className={cn("border-b last:border-0 hover:bg-accent/40", isSel && "bg-primary/5")}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${fullName(u.first_name, u.last_name)}`}
                      className="cursor-pointer align-middle"
                      checked={isSel}
                      onClick={(e) => onRowSelect(e, u, idx)}
                      onChange={() => { /* selection handled in onClick (needs shiftKey) */ }}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">{fullName(u.first_name, u.last_name)}</td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">{formatPhone(u.phone_number)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.city?.trim() || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[16rem] truncate" title={u.member_reason || ""}>{u.member_reason?.trim() || "—"}</td>
                  <td className="px-4 py-3"><TypeBadges u={u} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{u.referral || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatTimestamp(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onToggleAccess(u)}
                      disabled={readOnly || isBusy}
                      title={readOnly ? "Read-only mode" : u.access ? "Revoke access" : "Approve access"}
                      className={cn(
                        "inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border disabled:opacity-50",
                        u.access ? "bg-green-100 text-green-700 border-green-200 hover:bg-green-200"
                                 : "bg-muted text-muted-foreground border-border hover:bg-accent",
                      )}
                    >
                      {u.access ? <ShieldCheck className="size-3" /> : <ShieldX className="size-3" />}
                      {isBusy ? "…" : u.access ? "Approved" : "Off"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onToggleFounding(u)}
                      disabled={readOnly || isBusy}
                      title={readOnly ? "Read-only mode" : u.founding ? "Remove founding" : "Mark founding"}
                      className={cn(
                        "inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border disabled:opacity-50",
                        u.founding ? "bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200"
                                   : "bg-muted text-muted-foreground border-border hover:bg-accent",
                      )}
                    >
                      <Crown className="size-3" />
                      {isBusy ? "…" : u.founding ? "Founding" : "No"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {u.is_member ? planLabel(u.subscription_plan, u.subscription_status) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <NotesCell
                      note={notes[u.phone_number] ?? ""}
                      meta={notesMeta[u.phone_number]}
                      disabled={readOnly}
                      onSave={(text) => saveNote(u.phone_number, text)}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setConfirmDelete(u)}
                      disabled={readOnly || isBusy}
                      title={readOnly ? "Read-only mode" : "Delete this user's data"}
                      className="inline-flex items-center justify-center size-7 rounded text-muted-foreground hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      {isBusy ? "…" : <Trash2 className="size-3.5" />}
                    </button>
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr><td colSpan={13} className="px-4 py-8 text-center text-muted-foreground">{search ? "No users match your filter" : "No users yet"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{rows.length} shown of {users.length} total.</p>

      {notesLocalOnly && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
          <AlertTriangle className="size-3.5 shrink-0" />
          Notes are in browser-only mode: the backend doesn't have the notes endpoints yet, so notes save to this browser and are NOT visible to other admins. They'll switch to shared automatically once the backend is deployed.
        </div>
      )}

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onAdded={fetchAll} />}

      {confirmDelete && (
        <SafetyDialog
          title={`Delete ${fullName(confirmDelete.first_name, confirmDelete.last_name)}?`}
          description={
            confirmDelete.is_member
              ? "This permanently deletes their ACCOUNT (profile, seasons, conversations, auth login — everything) plus their waitlist/allowlist entries. A pre-delete snapshot is kept server-side. Cannot be undone."
              : "This removes their waitlist and allowlist (access) entries. They are not an app user, so there is no account to delete."
          }
          warnings={
            confirmDelete.is_member
              ? ["Full account + all data wiped — only for test users.", `Phone: ${formatPhone(confirmDelete.phone_number)}`]
              : [`Phone: ${formatPhone(confirmDelete.phone_number)}`]
          }
          confirmLabel="Delete"
          variant="destructive"
          typeToConfirm={confirmDelete.is_member ? "DELETE" : undefined}
          onConfirm={() => {
            const u = confirmDelete
            setConfirmDelete(null)
            doDelete(u)
          }}
          onCancel={() => setConfirmDelete(null)}
          isLoading={busy.has(confirmDelete.phone_number)}
        />
      )}

      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 rounded-xl border bg-popover text-popover-foreground shadow-lg px-3 py-2">
          <span className="text-sm font-medium px-1">{selected.size} selected</span>
          <span className="w-px h-5 bg-border" />
          <button onClick={() => bulkAccess(true)} disabled={readOnly || bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border hover:bg-accent disabled:opacity-50" title="Approve access">
            <ShieldCheck className="size-3.5 text-green-600" /> Approve
          </button>
          <button onClick={() => setConfirmBulk("revoke")} disabled={readOnly || bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border hover:bg-accent disabled:opacity-50" title="Revoke access">
            <ShieldX className="size-3.5" /> Revoke
          </button>
          <button onClick={() => bulkFounding(true)} disabled={readOnly || bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border hover:bg-accent disabled:opacity-50" title="Mark founding (free)">
            <Crown className="size-3.5 text-purple-600" /> Founding
          </button>
          <button onClick={() => bulkFounding(false)} disabled={readOnly || bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border hover:bg-accent disabled:opacity-50" title="Unmark founding">
            <Crown className="size-3.5" /> Unfounding
          </button>
          <button onClick={bulkExport} disabled={bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border hover:bg-accent disabled:opacity-50" title="Export selected to CSV">
            <Download className="size-3.5" /> Export
          </button>
          <button onClick={() => setConfirmBulk("delete")} disabled={readOnly || bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50" title="Delete selected">
            <Trash2 className="size-3.5" /> {bulkBusy ? "…" : "Delete"}
          </button>
          <span className="w-px h-5 bg-border" />
          <button onClick={clearSelection} className="text-muted-foreground hover:text-foreground p-1" title="Clear selection" aria-label="Clear selection">
            <X className="size-4" />
          </button>
        </div>
      )}

      {confirmBulk === "revoke" && (
        <SafetyDialog
          title={`Revoke access for ${selected.size} user(s)?`}
          description="Sets allowed=false on each selected phone. Under invite-only, anyone signing up fresh with these numbers would be sent to the waitlist. Existing users keep their access."
          warnings={[`${selected.size} users affected.`]}
          confirmLabel="Revoke access"
          variant="destructive"
          onConfirm={() => { setConfirmBulk(null); bulkAccess(false) }}
          onCancel={() => setConfirmBulk(null)}
          isLoading={bulkBusy}
        />
      )}
      {confirmBulk === "delete" && (
        <SafetyDialog
          title={`Delete ${selected.size} user(s)?`}
          description={
            selectionHasMember
              ? "Permanently deletes data for every selected user. For users this wipes the full ACCOUNT (profile, seasons, conversations, auth) plus waitlist/allowlist; pre-delete snapshots are kept. Cannot be undone."
              : "Removes the selected users' waitlist + allowlist entries. None are app users, so no accounts are deleted."
          }
          warnings={[
            `${selected.size} users will be deleted.`,
            ...(selectionHasMember ? ["Selection includes user account(s) — full wipe."] : []),
          ]}
          confirmLabel={`Delete ${selected.size}`}
          variant="destructive"
          typeToConfirm={selectionHasMember ? "DELETE" : undefined}
          onConfirm={() => { setConfirmBulk(null); bulkDelete() }}
          onCancel={() => setConfirmBulk(null)}
          isLoading={bulkBusy}
        />
      )}
    </div>
  )
}
